import { ref, computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { route, ShortCodeService } from '@ht/lib-plural-vue';
import { helpers } from '@ht/lib-plural-js';
import {
  SYSTEM_VARIABLES,
  SYSTEM_VARIABLES_MAP,
  VARIABLE_VALUE_TYPES,
  FLOW_STATE_OPTIONS,
  START_FLOW_ID,
} from '@/js/constants';
import { setDeviceId } from '../api';

const emptyConfig = {
  organisationId: '',
  organisationName: '',
  robotId: '',
  robotName: '',
  projectId: '',
  projectName: '',
  frameId: '',
  frameName: '',
  contentLanguages: [],
  iot: [],
  externalCommands: {},
  avatar: '',
  speechServicesActive: false,
  ageGenderDetectionActive: false,
  chatbotCredentials: [],
  forcePreview: false,
  credentials: {
    hash: '',
    salt: '',
  },
  companyLogo: null,
  ttsSubtitles: false,
  sttSubtitles: false,
  customPayload: {},
  assetsBaseUrl: '',
  avatarSettings: {},
  fonts: {
    custom: [],
    google: [],
  },
  defaultStyles: {},
  isBottomNavigationEnabled: false,
  bottomNavigationPosition: 'center',
};

const configStore = ref({});
const sharedConfig = ref({
  initialAvatar: null,
  variables: [],
  returnStack: [],
  deviceId: null,
});

const activeAvatar = ref(null);

const showLanguageSwitch = ref(true);
const currentLanguage = ref('');
const languageDir = ref('auto');

export default function useConfig() {
  const initialConfig = computed(() => configStore.value[sharedConfig.value.initialAvatar] ?? emptyConfig);
  const currentConfig = computed(() => configStore.value[activeAvatar.value] ?? emptyConfig);
  const defaultStyles = computed(() => configStore.value[activeAvatar.value]?.defaultStyles ?? {});

  function getVariable(id) {
    return sharedConfig.value.variables.find((v) => v.id === id);
  }
  function getVariableByName(name) {
    return sharedConfig.value.variables.find((v) => v.name === name);
  }

  function setVariables(variables) {
    variables.forEach(({ id, value }) => {
      const variable = getVariable(id);

      if (value.type === VARIABLE_VALUE_TYPES.VARIABLE) {
        const valueVariable = getVariable(value.value);
        if (valueVariable) {
          if (valueVariable.compareValue) {
            variable.compareValue = valueVariable.compareValue;
          }

          variable.value = valueVariable.value
          && helpers.isPrimitive(valueVariable.value)
          && helpers.isString(valueVariable.value)
            ? new ShortCodeService(valueVariable.value).matchAttributes(false).getValue()
            : valueVariable.value;
          return;
        }
      }

      if (variable) {
        variable.value = value.value
          && helpers.isPrimitive(value.value)
          && helpers.isString(value.value)
          ? new ShortCodeService(value.value)
            .matchAttributes(false)
            .getValue()
          : value.value;
      }
    });
  }

  function resetVariables(variables) {
    variables.forEach((id) => {
      const variable = getVariable(id);
      if (variable) {
        variable.value = '';
      }
    });
  }

  function isPreview() {
    return route.query.m === 'preview' || !!currentConfig.value.forcePreview;
  }

  function setFlowState(state) {
    setVariables([
      {
        id: SYSTEM_VARIABLES_MAP.FLOW_STATE.id,
        value: { value: state, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      },
    ]);
  }

  function setFlowStateStart() {
    setFlowState(FLOW_STATE_OPTIONS.SYSTEM_START);
  }

  function setFlowStateBackToStart(flowId) {
    if (flowId === START_FLOW_ID) {
      setFlowState(FLOW_STATE_OPTIONS.SYSTEM_BACK_TO_START);
    }
  }

  function setFlowStateHomeButton() {
    setFlowState(FLOW_STATE_OPTIONS.USER_HOME_BUTTON);
  }

  function setFlowStateUserLanguageSwitch() {
    setFlowState(FLOW_STATE_OPTIONS.USER_LANGUAGE_SWITCH);
  }

  function setFlowStateSystemLanguageSwitch() {
    setFlowState(FLOW_STATE_OPTIONS.SYSTEM_LANGUAGE_SWITCH);
  }

  function setLanguage(value) {
    currentLanguage.value = value;
    const langConfig = currentConfig.value.contentLanguages.find((l) => l.code === value);
    setVariables([
      {
        id: SYSTEM_VARIABLES_MAP.LANGUAGE_NAME.id,
        value: { value: langConfig.name, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      },
      {
        id: SYSTEM_VARIABLES_MAP.LANGUAGE_ISO1.id,
        value: { value: langConfig.iso639_1, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      },
      {
        id: SYSTEM_VARIABLES_MAP.LANGUAGE_ISO2.id,
        value: { value: langConfig.iso639_2, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      },
    ]);
  }

  function setActiveAvatar(avatarId) {
    const nextAvatarId = avatarId || sharedConfig.value.initialAvatar;

    if (activeAvatar.value !== nextAvatarId) {
      activeAvatar.value = nextAvatarId;
      if (!currentConfig.value.contentLanguages.some((l) => l.code === currentLanguage.value)) {
        const newPrimaryLanguage = currentConfig.value.contentLanguages.find((l) => l.isDefault)?.code;
        setLanguage(newPrimaryLanguage);
      }

      setVariables([{
        id: SYSTEM_VARIABLES_MAP.DB_TOKEN.id,
        value: { value: configStore.value[nextAvatarId].databaseToken, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }]);
    }
  }

  function setConfig(newAvatarId, data) {
    if (!sharedConfig.value.initialAvatar) {
      sharedConfig.value.initialAvatar = newAvatarId;
      activeAvatar.value = newAvatarId;
      sharedConfig.value.variables.push(...SYSTEM_VARIABLES);

      const currentLangConfig = data.config.contentLanguages.find((l) => l.isDefault);
      currentLanguage.value = currentLangConfig.code;

      setVariables([{
        id: SYSTEM_VARIABLES_MAP.SESSION_ID.id,
        value: { value: uuidv4(), type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.SESSION_START_TIME.id,
        value: { value: new Date(), type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.ROBOT_ID.id,
        value: { value: route.getRobotToken(), type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.ROBOT_NAME.id,
        value: { value: data.config.robotName, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.LANGUAGE_NAME.id,
        value: { value: currentLangConfig.name, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.LANGUAGE_ISO1.id,
        value: { value: currentLangConfig.iso639_1, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.LANGUAGE_ISO2.id,
        value: { value: currentLangConfig.iso639_2, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.AVAILABLE_LANGUAGES.id,
        value: { value: data.config.contentLanguages.map((l) => l.iso639_1), type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.ENABLED_LANGUAGES.id,
        value: { value: data.config.contentLanguages.map((l) => l.iso639_1), type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.KB_CONVERSATION_HISTORY.id,
        value: { value: [], type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.KB_THREAD_ID.id,
        value: { value: '', type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }, {
        id: SYSTEM_VARIABLES_MAP.DB_TOKEN.id,
        value: { value: data.config.databaseToken, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      }]);

      setFlowStateStart();

      let deviceId;
      try {
        deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
          deviceId = uuidv4();
          localStorage.setItem('deviceId', deviceId);
        }
      } catch (err) {
        deviceId = uuidv4();
      }
      setDeviceId(deviceId);
      sharedConfig.value.deviceId = deviceId;
    }

    configStore.value[newAvatarId] = {
      ...data.config,
      variables: undefined,
      assetsBaseUrl: data.assets.baseUrl,
      defaultStyles: data.defaultStyles || {},
      speechServicesActive: data.config.speechServicesActive && !isPreview(),
      // age/gender detection is no longer supported, the export setting is ignored on purpose
      ageGenderDetectionActive: false,
      contentLanguages: data.config.contentLanguages
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((langOptions) => ({ ...langOptions, active: true })),
    };

    sharedConfig.value.variables.push(...data.config.variables
      .filter((v) => !sharedConfig.value.variables.some((gv) => v.name === gv.name))
      .map((v) => ({ ...v, value: v.value || '' })));
  }

  function setLanguageActive(languageCode, active) {
    const langConfig = currentConfig.value.contentLanguages.find((l) => l.iso639_1 === languageCode);
    if (!langConfig) {
      return;
    }
    if (!active && langConfig.code === currentLanguage.value) {
      return;
    }
    langConfig.active = active;
    setVariables([{
      id: SYSTEM_VARIABLES_MAP.ENABLED_LANGUAGES.id,
      value: {
        value: currentConfig.value.contentLanguages
          .filter((l) => l.active)
          .map((l) => l.iso639_1),
        type: VARIABLE_VALUE_TYPES.PRIMITIVE,
      },
    }]);
  }

  function setShowLanguageSwitch(flag) {
    showLanguageSwitch.value = flag;
  }

  function getCurrentDfLanguage() {
    return currentConfig.value.contentLanguages.find((l) => l.code === currentLanguage.value).dfLang;
  }

  function getCurrentSttLanguage() {
    if (currentConfig.value.sttSettings) {
      return currentConfig.value.sttSettings[currentLanguage.value];
    }
    return { languageCode: getCurrentDfLanguage() };
  }

  function setVariablesFromQuery() {
    const { query } = route;
    Object.keys(query).forEach((variableName) => {
      if (SYSTEM_VARIABLES.some((sv) => sv.name === variableName)) {
        return;
      }
      const variable = getVariableByName(variableName);
      if (variable) {
        variable.value = query[variableName];
      }
    });
  }

  function startNewSession() {
    setVariables([
      {
        id: SYSTEM_VARIABLES_MAP.SESSION_ID.id,
        value: { value: uuidv4(), type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      },
      {
        id: SYSTEM_VARIABLES_MAP.SESSION_START_TIME.id,
        value: { value: new Date(), type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      },
    ]);
  }

  function popReturnStack() {
    const previousProject = sharedConfig.value.returnStack.pop();
    if (previousProject) {
      setActiveAvatar(previousProject.token);
      return previousProject.next;
    }
    return null;
  }

  function pushReturnStack(next) {
    sharedConfig.value.returnStack.push({
      token: activeAvatar.value,
      next,
    });
  }

  return {
    setConfig,
    setActiveAvatar,
    activeAvatar,
    currentConfig,
    initialConfig,
    sharedConfig,
    defaultStyles,
    currentLanguage,
    languageDir,
    showLanguageSwitch,
    setShowLanguageSwitch,
    getVariable,
    getVariableByName,
    setVariables,
    resetVariables,
    setLanguage,
    setLanguageActive,
    getCurrentDfLanguage,
    getCurrentSttLanguage,
    isPreview,
    setVariablesFromQuery,
    startNewSession,
    setFlowState,
    setFlowStateHomeButton,
    setFlowStateStart,
    setFlowStateBackToStart,
    setFlowStateUserLanguageSwitch,
    setFlowStateSystemLanguageSwitch,
    popReturnStack,
    pushReturnStack,
  };
}
